import { z } from "zod";

// ---------------------------------------------------------------------------
// Queue priority policy (docs/NEXT.md — Queue priority policy).
//
// A small *closed* vocabulary of strategies, parameterized by UI-editable
// data, stored as the shared `queue-priority-policy` config document and
// read by both the Next index and the Cosmos fallback. The strategy token
// is the wire contract: `absolute` queues occupy a hard top tier ordered by
// `absoluteRank` (lower wins) and out-rank any scored queue structurally;
// `dynamic` queues sit in the scored floor (tier 0). Personal queues with no
// explicit override fall back to `personalQueueDefault`.
//
// `strategy` is kept a free string (not a Zod enum) at the boundary: an
// unknown future strategy token should render in the editor as an
// unrecognized value the admin can see and replace, not crash the parse —
// the backend remains the authority that rejects an unknown token on write.
// ---------------------------------------------------------------------------

export const QUEUE_PRIORITY_STRATEGIES = ["dynamic", "absolute"] as const;
export type QueuePriorityStrategy = (typeof QUEUE_PRIORITY_STRATEGIES)[number];

export const QueuePriorityAssignmentSchema = z.object({
    strategy: z.string(),
    absoluteRank: z.number().int(),
});

export type QueuePriorityAssignment = z.infer<typeof QueuePriorityAssignmentSchema>;

export const QueuePriorityOverrideSchema = z.object({
    queueId: z.string(),
    strategy: z.string(),
    absoluteRank: z.number().int(),
});

export type QueuePriorityOverride = z.infer<typeof QueuePriorityOverrideSchema>;

export const QueuePriorityPolicyResponseSchema = z.object({
    personalQueueDefault: QueuePriorityAssignmentSchema,
    overrides: z.array(QueuePriorityOverrideSchema),
    updatedAt: z.string().nullable().optional().default(null),
    updatedBy: z.string().nullable().optional().default(null),
});

export type QueuePriorityPolicyResponse = z.infer<typeof QueuePriorityPolicyResponseSchema>;

// ---------------------------------------------------------------------------
// Task-type weights (docs/NEXT.md — Task-type weights).
//
// The TaskTypes *vocabulary* is a published contract; the *weights* are
// operational tuning data living in their own config doc. A known task type
// with no weight row scores at `neutralWeight` (1.0). `knownTaskTypes` is the
// server's authoritative enumeration of the closed vocabulary — the editor
// renders a row per known type and lets the admin set a positive multiplier,
// rather than inventing keys client-side.
// ---------------------------------------------------------------------------

export const TaskTypeWeightsResponseSchema = z.object({
    weights: z.record(z.string(), z.number()),
    knownTaskTypes: z.array(z.string()),
    neutralWeight: z.number(),
    updatedAt: z.string().nullable().optional().default(null),
    updatedBy: z.string().nullable().optional().default(null),
});

export type TaskTypeWeightsResponse = z.infer<typeof TaskTypeWeightsResponseSchema>;
